import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const EXAMPLE_NAMES = 'e.g. Show String Base, Senior Program, Easy Keeper, Broodmare Program';
const SUPPLEMENT_UNITS = [
  { value: 'scoop', label: 'Scoop' },
  { value: 'teaspoon', label: 'Teaspoon' },
  { value: 'tablespoon', label: 'Tablespoon' },
  { value: 'gram', label: 'Gram (g)' },
  { value: 'mg', label: 'Milligram (mg)' },
  { value: 'oz', label: 'Ounce (oz)' },
  { value: 'ml', label: 'mL / cc' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'packet', label: 'Packet' },
  { value: 'pump', label: 'Pump' },
  { value: 'drop', label: 'Drop' },
  { value: 'serving', label: 'Serving' },
  { value: 'custom', label: 'Custom' },
];
const unitLabel = (v) => SUPPLEMENT_UNITS.find(u => u.value === v)?.label || v;
const amountDetail = (item) => [item.dose && `${item.dose} ${unitLabel(item.unit || 'scoop')}`, item.soaked ? 'soaked' : null].filter(Boolean).join(' · ');
const formatStackTime = (time) => {
  if (!time) return 'No time set';
  const [h, m] = String(time).split(':');
  const date = new Date();
  date.setHours(parseInt(h || '0', 10), parseInt(m || '0', 10), 0, 0);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const slotLabel = (slot) => ({ morning: 'Morning', midday: 'Midday', evening: 'Evening', custom: 'Custom' }[slot] || 'Morning');

// Add an item to a stack — reuses the supplement catalog search.
// No item-level frequency: the stack's feeding time controls timing.
const AddItemModal = ({ stackId, onClose, onSaved }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [dose, setDose] = useState('');
  const [unit, setUnit] = useState('scoop');
  const [soaked, setSoaked] = useState(false);
  const [notes, setNotes] = useState('');
  const [reorderEnabled, setReorderEnabled] = useState(false);
  const [cycleDays, setCycleDays] = useState(30);
  const [leadDays, setLeadDays] = useState(5);
  const [trackUsage, setTrackUsage] = useState(true);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const runSearch = useCallback((q) => {
    setSearching(true);
    api.get(`/catalog/search?category=supplement&q=${encodeURIComponent(q)}`)
      .then(r => setResults(r.data.results || [])).catch(() => setResults([])).finally(() => setSearching(false));
  }, []);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const noMatch = query.trim() && !searching && results.length === 0;

  const handleSave = async () => {
    const name = picked ? picked.name : query.trim();
    if (!name) return toast.error('Choose or type a supplement');
    setSaving(true);
    try {
      if (!picked) await api.post('/catalog/private', { name, category: 'supplement' });
      await api.post(`/stacks/${stackId}/items`, {
        name,
        subcategory: picked?.subcategory,
        dose,
        unit,
        soaked,
        notes,
        reorder_enabled: reorderEnabled,
        reorder_cycle_days: cycleDays,
        reorder_lead_days: leadDays,
        reorder_recurring: true,
        reorder_track_usage: trackUsage,
      });
      toast.success('Added to stack');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2>Add supplement to stack</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        {!picked && (
          <>
            <div className="form-group">
              <label className="form-label">Search supplements</label>
              <input className="form-input" value={query} onChange={e => setQuery(e.target.value)} placeholder="Start typing, e.g. Cosequin" autoComplete="off" autoFocus />
            </div>
            {searching && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Searching…</div>}
            {results.length > 0 && (
              <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, maxHeight: 240, overflowY: 'auto', marginBottom: 12 }}>
                {results.map(r => (
                  <div key={r.id} onClick={() => setPicked(r)} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{r.name}</div>
                    {r.subcategory && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.subcategory}</div>}
                  </div>
                ))}
              </div>
            )}
            {noMatch && <div style={{ fontSize: 13, marginBottom: 12 }}>No match — "{query.trim()}" will be added as your own item.</div>}
          </>
        )}
        {(picked || noMatch) && (
          <>
            <div style={{ background: 'var(--cr-primary-light)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{picked ? picked.name : query.trim()}</div>
              <button onClick={() => setPicked(null)} style={{ background: 'none', border: 'none', color: 'var(--cr-primary)', cursor: 'pointer', fontSize: 12, marginTop: 4, padding: 0 }}>Change</button>
            </div>
            <div className="form-group">
              <label className="form-label">Dose / amount</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input className="form-input" value={dose} onChange={e => setDose(e.target.value)} placeholder="e.g. 1" inputMode="decimal" />
                <select className="form-input" value={unit} onChange={e => setUnit(e.target.value)}>
                  {SUPPLEMENT_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '4px 0 10px' }}>
              <input type="checkbox" checked={soaked} onChange={e => setSoaked(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
              Soaked
            </label>
            <div className="form-group"><label className="form-label">Notes / directions (optional)</label><input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. mix with wet feed" /></div>
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
              Timing is set by the stack's Morning/Midday/Evening time above. No separate supplement frequency is needed here.
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              <input type="checkbox" checked={reorderEnabled} onChange={e => setReorderEnabled(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
              Set up reorder reminder
            </label>
            {reorderEnabled && (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div className="form-group"><label className="form-label">Reorder every</label>
                  <select className="form-input" value={cycleDays} onChange={e => setCycleDays(parseInt(e.target.value))}>
                    <option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option>
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Remind ahead</label>
                  <select className="form-input" value={leadDays} onChange={e => setLeadDays(parseInt(e.target.value))}>
                    <option value={3}>3 days</option><option value={5}>5 days</option><option value={7}>7 days</option><option value={14}>14 days</option>
                  </select>
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={trackUsage} onChange={e => setTrackUsage(e.target.checked)} style={{ accentColor: 'var(--cr-primary)', marginTop: 2 }} />
                  <span>Track usage — learns and adjusts timing from feedback.</span>
                </label>
              </div>
            )}
            <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Add to stack'}</button>
          </>
        )}
      </div>
    </div>
  );
};


const StackSettingsModal = ({ stack, onClose, onSaved }) => {
  const [name, setName] = useState(stack.name || '');
  const [slot, setSlot] = useState(stack.default_slot || 'morning');
  const [time, setTime] = useState(stack.default_time || '');
  const [alertEnabled, setAlertEnabled] = useState(!!stack.default_alert);
  const [notes, setNotes] = useState(stack.notes || '');
  const [updateApplied, setUpdateApplied] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return toast.error('Stack name required');
    setSaving(true);
    try {
      const { data } = await api.put(`/stacks/${stack.id}`, {
        name: name.trim(),
        default_slot: slot,
        default_time: time || null,
        default_alert: alertEnabled,
        notes,
        update_applied: updateApplied,
      });
      toast.success(updateApplied ? 'Stack settings and applied horse reminders updated' : 'Stack settings updated');
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2>Edit stack settings</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Stack name</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)} />
        </div>
        <label className="form-label">Default feeding time</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <select className="form-input" value={slot} onChange={e => setSlot(e.target.value)}>
            <option value="morning">Morning</option>
            <option value="midday">Midday</option>
            <option value="evening">Evening</option>
            <option value="custom">Custom</option>
          </select>
          <input className="form-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '6px 0 10px' }}>
          <input type="checkbox" checked={alertEnabled} onChange={e => setAlertEnabled(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
          Show this stack in Home notices at this time
        </label>
        <div className="form-group">
          <label className="form-label">Notes / directions</label>
          <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13, padding: '4px 0 14px' }}>
          <input type="checkbox" checked={updateApplied} onChange={e => setUpdateApplied(e.target.checked)} style={{ accentColor: 'var(--cr-primary)', marginTop: 2 }} />
          <span>Also update horse copies that were already applied from this stack.</span>
        </label>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
      </div>
    </div>
  );
};

const ApplyModal = ({ stack, horses, onClose, onApplied }) => {
  const [selected, setSelected] = useState('all');
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleApply = async () => {
    if (selected === 'selected' && picked.length === 0) return toast.error('Select at least one horse');
    setSaving(true);
    try {
      const { data } = await api.post(`/stacks/${stack.id}/apply`, { horse_ids: selected === 'all' ? 'all' : picked });
      toast.success(data.message);
      onApplied();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2>Apply "{stack.name}"</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>Each horse gets its own editable copy of these supplements at this stack's set time. Adjust dose, unit, soaked status, or notes afterward on the individual horse page without changing this saved stack.</p>
        <div className="chip-group" style={{ marginBottom: 12 }}>
          <div className={`chip ${selected === 'all' ? 'selected' : ''}`} onClick={() => setSelected('all')}>All horses</div>
          <div className={`chip ${selected === 'selected' ? 'selected' : ''}`} onClick={() => setSelected('selected')}>Select horses</div>
        </div>
        {selected === 'selected' && (
          <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 8, marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
            {horses.map(h => (
              <label key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(h.id)} onChange={() => toggle(h.id)} style={{ accentColor: 'var(--cr-primary)' }} />
                {h.name}
              </label>
            ))}
          </div>
        )}
        <button className="btn btn-primary btn-full" onClick={handleApply} disabled={saving}>{saving ? 'Applying…' : 'Apply stack'}</button>
      </div>
    </div>
  );
};

const SupplementStacksPage = () => {
  const navigate = useNavigate();
  const [stacks, setStacks] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newSlot, setNewSlot] = useState('morning');
  const [newTime, setNewTime] = useState('');
  const [newAlert, setNewAlert] = useState(false);
  const [openStack, setOpenStack] = useState(null);
  const [addingItem, setAddingItem] = useState(false);
  const [applying, setApplying] = useState(null);
  const [editingStack, setEditingStack] = useState(null);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([api.get('/stacks'), api.get('/horses')]);
      setStacks(s.data); setHorses(h.data);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createStack = async () => {
    if (!newName.trim()) return toast.error('Name your stack');
    try {
      const { data } = await api.post('/stacks', { name: newName, default_slot: newSlot, default_time: newTime, default_alert: newAlert });
      setNewName(''); setNewTime(''); setNewAlert(false);
      await load();
      setOpenStack({ ...data, items: [] });
    } catch { toast.error('Failed'); }
  };

  const openStackDetail = async (id) => {
    try { const { data } = await api.get(`/stacks/${id}`); setOpenStack(data); } catch { toast.error('Failed'); }
  };

  const deleteItem = async (itemId) => {
    try { await api.delete(`/stacks/items/${itemId}`); openStackDetail(openStack.id); } catch { toast.error('Failed'); }
  };

  const deleteStack = async (id) => {
    let usage = { horses: 0, supplements: 0 };
    try { const { data } = await api.get(`/stacks/${id}/usage`); usage = data || usage; } catch { /* fall back to plain delete */ }

    let removeApplied = false;
    if (usage.supplements > 0) {
      const horseWord = usage.horses === 1 ? 'horse' : 'horses';
      const suppWord = usage.supplements === 1 ? 'supplement' : 'supplements';
      removeApplied = window.confirm(
        `This stack is applied to ${usage.horses} ${horseWord} (${usage.supplements} ${suppWord}).\n\n` +
        `OK  = delete the stack AND remove those supplements from the horses\n` +
        `Cancel = keep the supplements on the horses`
      );
      if (!window.confirm(removeApplied
        ? `Delete stack and remove ${usage.supplements} ${suppWord} from ${usage.horses} ${horseWord}?`
        : 'Delete the stack and leave the horses unchanged?')) return;
    } else {
      if (!window.confirm('Delete this stack?')) return;
    }

    try {
      const { data } = await api.delete(`/stacks/${id}?remove_applied=${removeApplied}`);
      const r = (data && data.removed) || {};
      if (r.supplements > 0) toast.success(`Deleted. Removed ${r.supplements} supplement${r.supplements !== 1 ? 's' : ''} from horses.`);
      setOpenStack(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const handleStackSettingsSaved = (updated) => {
    setEditingStack(null);
    setOpenStack(prev => prev && prev.id === updated.id ? { ...prev, ...updated, items: prev.items || updated.items || [] } : prev);
    load();
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (openStack) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
        <div className="top-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => { setOpenStack(null); load(); }} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
            <h1 style={{ color: 'white', fontSize: 17, flex: 1 }}>{openStack.name}</h1>
          </div>
        </div>
        <div style={{ padding: 12 }}>
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>⏰</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{formatStackTime(openStack.default_time)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {slotLabel(openStack.default_slot)} · {openStack.default_alert ? 'Shows on Home notices' : 'Home notice off'}
              </div>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setEditingStack(openStack)}>Edit</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setAddingItem(true)}>+ Add supplement</button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setApplying(openStack)} disabled={(openStack.items || []).length === 0}>Apply to horses</button>
          </div>
          {(openStack.items || []).length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">💊</div><h3>Empty stack</h3><p>Add the supplements that make up this program.</p></div>
          ) : (
            openStack.items.map(it => (
              <div key={it.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>💊</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{it.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{[amountDetail(it), it.notes, it.reorder_enabled ? `reorder ${it.reorder_cycle_days}d` : null].filter(Boolean).join(' · ')}</div>
                </div>
                <button onClick={() => deleteItem(it.id)} className="btn btn-danger btn-sm">Remove</button>
              </div>
            ))
          )}
          <button className="btn btn-danger btn-full" style={{ marginTop: 16 }} onClick={() => deleteStack(openStack.id)}>Delete stack</button>
        </div>
        {addingItem && <AddItemModal stackId={openStack.id} onClose={() => setAddingItem(false)} onSaved={() => { setAddingItem(false); openStackDetail(openStack.id); }} />}
        {applying && <ApplyModal stack={applying} horses={horses} onClose={() => setApplying(null)} onApplied={() => setApplying(null)} />}
        {editingStack && <StackSettingsModal stack={editingStack} onClose={() => setEditingStack(null)} onSaved={handleStackSettingsSaved} />}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/horses')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
          <h1 style={{ color: 'white', fontSize: 17, flex: 1 }}>Supplement stacks</h1>
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <div className="card" style={{ background: 'var(--cr-primary-light)' }}>
          <p style={{ fontSize: 13 }}>Multiple horses on the same supplement program? Build a stack here and apply it to all or selected horses. After applying, each horse gets its own editable copy. The stack time controls the reminder, so item-level frequency is not needed.</p>
        </div>

        <div className="card">
          <label className="form-label">New stack name</label>
          <input className="form-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder={EXAMPLE_NAMES} autoComplete="off" />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', margin: '6px 0 10px' }}>{EXAMPLE_NAMES}</div>
          <label className="form-label">Default feeding time (applied to each horse, editable after)</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <select className="form-input" value={newSlot} onChange={e => setNewSlot(e.target.value)}>
              <option value="morning">Morning</option><option value="midday">Midday</option><option value="evening">Evening</option><option value="custom">Custom</option>
            </select>
            <input className="form-input" type="time" value={newTime} onChange={e => setNewTime(e.target.value)} />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '8px 0' }}>
            <input type="checkbox" checked={newAlert} onChange={e => setNewAlert(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
            Set a supplement reminder at this time
          </label>
          <button className="btn btn-primary btn-full" onClick={createStack}>+ Create stack</button>
        </div>

        {stacks.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No stacks yet</h3><p>Create your first supplement program above.</p></div>
        ) : (
          stacks.map(s => (
            <div key={s.id} className="card" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }} onClick={() => openStackDetail(s.id)}>
              <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>📋</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{(s.items || []).length} supplement{(s.items || []).length !== 1 ? 's' : ''} · {formatStackTime(s.default_time)} · {s.default_alert ? 'notice on' : 'notice off'}</div>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: 20 }}>›</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SupplementStacksPage;
